import CryptoJS from 'crypto-js';

/**
 * Encrypted localStorage wrapper.
 *
 * WARNING: This is **obfuscation only**, not real security.
 * The encryption key is embedded in the JS bundle and accessible to any
 * code running on the page. This prevents casual inspection of localStorage
 * values but does NOT protect against XSS.
 *
 * For production-grade token storage, use httpOnly cookies set by the backend.
 */

const ENCRYPTION_KEY =
  process.env.REACT_APP_ENCRYPTION_KEY ?? 'CIMEQ-TEMPLATE-2024-SECRET-KEY-CHANGE-IN-PROD';
const DEFAULT_ENCRYPTION_KEY = 'CIMEQ-TEMPLATE-2024-SECRET-KEY-CHANGE-IN-PROD';

if (process.env.NODE_ENV === 'production' && ENCRYPTION_KEY === DEFAULT_ENCRYPTION_KEY) {
  console.error(
    'REACT_APP_ENCRYPTION_KEY must be set to a unique value in production. Token storage is not secure with the default key.'
  );
}
const STORAGE_PREFIX = '__enc__';

export const secureStorage = {
  setItem(key: string, value: string): void {
    try {
      if (!value) return;
      const encrypted = CryptoJS.AES.encrypt(value, ENCRYPTION_KEY).toString();
      localStorage.setItem(key, STORAGE_PREFIX + encrypted);
    } catch {
      localStorage.setItem(key, value);
    }
  },

  getItem(key: string): string | null {
    try {
      const storedValue = localStorage.getItem(key);
      if (!storedValue) return null;

      if (!storedValue.startsWith(STORAGE_PREFIX)) return storedValue;

      const encrypted = storedValue.substring(STORAGE_PREFIX.length);
      const decrypted = CryptoJS.AES.decrypt(encrypted, ENCRYPTION_KEY);
      const value = decrypted.toString(CryptoJS.enc.Utf8);
      return value || null;
    } catch {
      return null;
    }
  },

  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      /* localStorage indisponible */
    }
  },

  clear(): void {
    try {
      localStorage.clear();
    } catch {
      /* localStorage indisponible */
    }
  },

  hasItem(key: string): boolean {
    try {
      return localStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  },

  keys(): string[] {
    try {
      return Object.keys(localStorage);
    } catch {
      return [];
    }
  },
};

export const tokenStorage = {
  setAccessToken(token: string): void {
    secureStorage.setItem('accessToken', token);
  },
  getAccessToken(): string | null {
    return secureStorage.getItem('accessToken');
  },
  setRefreshToken(token: string): void {
    secureStorage.setItem('refreshToken', token);
  },
  getRefreshToken(): string | null {
    return secureStorage.getItem('refreshToken');
  },
  clearTokens(): void {
    secureStorage.removeItem('accessToken');
    secureStorage.removeItem('refreshToken');
  },
  hasTokens(): boolean {
    return secureStorage.hasItem('accessToken') && secureStorage.hasItem('refreshToken');
  },
};

export default secureStorage;
